<lib-sales-insights-card
  [cardTitle]="config?.title"
  [subtitle]="subtitle"
  [loading]="loading"
  [error]="error"
  [empty]="empty"
>
  @if (canExport) {
    <button cardActions type="button" class="btn btn-sm si-health__button" [disabled]="exporting" (click)="exportExcel()">
      <i class="fa-solid fa-file-excel"></i>{{ 'SALES_INSIGHTS_EXPORT_EXCEL' | translate }}
    </button>
  }
  <div cardToolbar class="si-health__toolbar">
    <label class="si-health__search">
      <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
      <input
        type="search"
        class="form-control"
        [value]="search"
        [placeholder]="'SALES_INSIGHTS_HEALTH_SEARCH' | translate"
        [attr.aria-label]="'SALES_INSIGHTS_HEALTH_SEARCH' | translate"
        (input)="onSearch($any($event.target).value)"
      />
    </label>
    <div class="si-health__select">
      <lib-gung-input-multi-select
        [optionsList]="flagOptions"
        [singleSelection]="true"
        [showEmptyIds]="true"
        [showId]="false"
        [suppressSearch]="true"
        previewField="name"
        (selectedValues)="selectFlag($event)"
      ></lib-gung-input-multi-select>
    </div>
    @if (refreshing) {
      <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
    }
    @if (!loading && !error) {
      <span class="si-health__count">
        {{ 'SALES_INSIGHTS_HEALTH_COUNT' | translate: { count: formatAmount(rows.length) } }}
      </span>
    }
  </div>
  <div class="table-responsive si-health">
    <table class="table table-sm table-hover mb-0">
      <thead>
        <tr>
          @for (column of columns; track column) {
            <th
              scope="col"
              class="si-health__sortable"
              [class.text-end]="isNumberColumn(column)"
              [attr.aria-sort]="sortColumn === column ? (sortDirection === 'asc' ? 'ascending' : 'descending') : null"
              (click)="sortBy(column)"
            >
              {{ columnLabelKey(column) | translate }}
              @if (sortColumn === column) {
                <i
                  class="fa-solid ms-1"
                  [class.fa-arrow-up-short-wide]="sortDirection === 'asc'"
                  [class.fa-arrow-down-wide-short]="sortDirection === 'desc'"
                ></i>
              }
            </th>
            @if (column === 'salesperson') {
              <th scope="col">{{ 'SALES_INSIGHTS_HEALTH_COL_STATUS' | translate }}</th>
            }
          }
          @if (canBook) {
            <th scope="col"><span class="visually-hidden">{{ 'SALES_INSIGHTS_BOOK_ACTIVITY' | translate }}</span></th>
          }
        </tr>
      </thead>
      <tbody>
        @for (row of visibleRows; track row.customerId) {
          <tr>
            <td class="si-health__customer">
              <span class="si-health__name">{{ row.customerName || row.customerId }}</span>
              <span class="si-health__sub">
                {{ row.customerId }}
                @if (row.email) {
                  <span class="si-health__email">
                    · <span class="si-health__email-value" [title]="row.email">{{ row.email }}</span>
                    <button
                      type="button"
                      class="si-health__copy"
                      [title]="'SALES_INSIGHTS_COPY_EMAIL' | translate"
                      [attr.aria-label]="'SALES_INSIGHTS_COPY_EMAIL' | translate"
                      (click)="copyEmail(row, $event)"
                    >
                      <i
                        class="fa-regular"
                        [class.fa-copy]="copiedCustomerId !== row.customerId"
                        [class.fa-check]="copiedCustomerId === row.customerId"
                      ></i>
                    </button>
                  </span>
                }
              </span>
            </td>
            <td>{{ row.city }}</td>
            <td class="si-health__nowrap">{{ salespersonName(row) }}</td>
            <td>
              <span class="si-health__flags">
                @for (flag of shownFlags(row); track flag) {
                  <span [class]="'si-health__chip si-health__chip--' + flagTone(flag)">
                    @if (flag === 'overdue') {
                      <i class="fa-regular fa-bell"></i>
                    }
                    {{ flagLabelKey(flag) | translate }}
                  </span>
                } @empty {
                  <span class="si-health__chip si-health__chip--success">
                    {{ (row.visitRequired ? 'SALES_INSIGHTS_FLAG_VISIT_REQUIRED' : 'SALES_INSIGHTS_FLAG_NO_VISIT_REQUIRED') | translate }}
                  </span>
                }
              </span>
            </td>
            <td class="si-health__nowrap">
              @if (row.lastPurchaseDate) {
                {{ row.lastPurchaseDate }}
                <span class="si-health__sub">{{ daysAgo(row.daysSinceLastPurchase) }}</span>
              } @else {
                <span class="si-health__none">{{ 'SALES_INSIGHTS_NO_PURCHASE' | translate }}</span>
              }
            </td>
            <td class="si-health__nowrap">
              @if (row.lastContact) {
                {{ row.lastContact.date }}
                <span class="si-health__sub">
                  {{ contactTypeName(row.lastContact) }}
                  @if (row.daysSinceLastContact !== null) {
                    · {{ daysAgo(row.daysSinceLastContact) }}
                  }
                </span>
              } @else {
                <span class="si-health__chip si-health__chip--danger">{{ 'SALES_INSIGHTS_NO_CONTACT' | translate }}</span>
              }
            </td>
            <td class="si-health__nowrap">
              @if (row.nextContact) {
                <span class="si-health__chip si-health__chip--neutral">
                  <i class="fa-regular fa-calendar"></i>{{ row.nextContact.date }}
                </span>
                <span class="si-health__sub">{{ contactTypeName(row.nextContact) }}</span>
              } @else if (row.flags?.includes('overdue')) {
                <span class="si-health__chip si-health__chip--danger">
                  <i class="fa-regular fa-bell"></i>{{ 'SALES_INSIGHTS_NEXT_CONTACT_OVERDUE' | translate }}
                </span>
              } @else if (row.visitRequired) {
                <span class="si-health__none">{{ 'SALES_INSIGHTS_NOT_BOOKED' | translate }}</span>
              }
            </td>
            <td class="text-end si-health__number">
              {{ formatAmount(row.invoicedYtd) }}
              @if (invoicedDelta(row); as delta) {
                <span
                  class="si-health__sub si-health__delta"
                  [class.si-health__delta--up]="delta.direction === 'up'"
                  [class.si-health__delta--down]="delta.direction === 'down'"
                  [title]="('SALES_INSIGHTS_COMPARED_TO' | translate) + ' ' + formatAmount(row.invoicedPrevYtd)"
                  >{{ delta.text }}</span
                >
              }
            </td>
            <td class="text-end si-health__number">
              @if (row.openOrderValue) {
                {{ formatAmount(row.openOrderValue) }}
              }
            </td>
            @if (canBook) {
              <td class="text-end">
                <lib-sales-insights-book-activity [customerId]="row.customerId"></lib-sales-insights-book-activity>
              </td>
            }
          </tr>
        }
      </tbody>
    </table>
    @if (rows.length > visibleCount) {
      <div class="si-health__more">
        <button type="button" class="btn btn-outline-secondary btn-sm" (click)="showMore()">
          {{ 'SALES_INSIGHTS_SHOW_MORE' | translate: { shown: visibleCount, total: rows.length } }}
        </button>
      </div>
    }
  </div>
</lib-sales-insights-card>
